import { useMelwaysLink } from '@seek/melways-react';
import { getHostName } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Column,
  Columns,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useNavigate,
  useParams,
  useRouteLoaderData,
  useSearchParams,
} from 'react-router';

import { CompanyProfileSummary } from '@seek/libs-shared';
import { CompanyProfilesPage, ROOT_PATH } from '../../../paths';
import { MainLayout } from '../mainLayout/MainLayout';
import { WriteReviewButton } from './WriteReview.tsx';

import {
  CompanyBrandingSection,
  useAppContext,
  useConfig,
  type CompanyBrandingSectionModel,
} from '@seek/libs-shared';
import type { AppConfig } from '../../../shared/config';
import { trackLinkEvents } from '../../../shared/tracking/candidateApp/trackLinkEvents';
import { getLocationContextualName } from '../../../shared/utils/getLocationContextualName';
import { CompanySearchBar } from '../../components/companySearchBar/CompanySearchBar';
import { getMetaData } from '../../components/MetaTag/meta';
import { MetaTag } from '../../components/MetaTag/MetaTag';
import { AboutPageContainer } from '../../pages/about/AboutPageContainer';
import { CulturePageContainer } from '../../pages/culture/CulturePageContainer';
import { JobsPageContainer } from '../../pages/jobs/JobsPageContainer';
import { ReviewsPageContainer } from '../../pages/reviews/ReviewsPageContainer';
import { CompanyProfileLayoutStickyHeader } from './CompanyProfileLayoutStickyHeader.tsx';

import { SalariesPageContainer } from '../../pages/salaries/SalariesPageContainer';
import translations from './.vocab';

import { useScrollToTop } from '../../../shared/utils/useScrollToTop';

import { HIDE_REVIEWS_COMPANY_IDS } from '../../../shared/constants/redirection';
import { useIsSalariesPageFeatureOn } from '../../../shared/utils/useIsSalariesPageFeatureOn';
import type { SalariesTabModel } from '../../pages/salaries/salariesTabModel';
import * as styles from './CompanyProfileLayout.css';

type Props = React.PropsWithChildren<{
  page: CompanyProfilesPage;
}>;

export function CompanyProfileLayout({ page }: Props) {
  const DEFAULT_TAB = CompanyProfilesPage.About;
  const { t } = useTranslations(translations);
  const { scrollToTop, scrollToTopRef } = useScrollToTop('PAGE');

  const companyProfileModel = useRouteLoaderData(
    'company-profile-root',
  ) as CompanyBrandingSectionModel;
  const salariesRolePageModel = useRouteLoaderData<SalariesTabModel>(
    CompanyProfilesPage.SalariesRole,
  );

  if (!companyProfileModel) {
    throw new Error(
      'Company profile model is not available. Please check the loader.',
    );
  }

  const [activeTab, setActiveTab] = React.useState<string | undefined>(page);
  const [showReviewReplyNotice, setShowReviewReplyNotice] =
    React.useState<boolean>(false);
  const [isDraft, setIsDraft] = useState(false);
  const [shouldShowBackToJobSearchLink, setShouldShowBackToJobSearchLink] =
    useState<boolean>(false);

  useEffect(() => {
    if (activeTab !== page) {
      setActiveTab(page);
      scrollToTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page]);

  const config = useConfig<AppConfig>();
  const {
    language,
    siteName: site,
    environment,
    absoluteUrl,
    consolidated,
  } = config;

  const navigate = useNavigate();
  const { companySlug, locationSlug, roleId } = useParams();
  const [params] = useSearchParams();
  const context = useAppContext();
  const { setCompanyId, setCompanyName } = context;
  const localize = useMelwaysLink();

  const shouldShowSalariesPage = useIsSalariesPageFeatureOn();

  const shouldHideReview = HIDE_REVIEWS_COMPANY_IDS.includes(
    companyProfileModel.id,
  );

  const locationContextualName = getLocationContextualName({
    locationSlug,
    config,
  });

  const isSalariesRoleRoute = roleId !== undefined;
  let roleName = '';
  if (isSalariesRoleRoute) {
    const currentRole =
      salariesRolePageModel?.salaryRanges?.salaryRangeByRoles?.find(
        (role) => role.roleId === roleId,
      );
    roleName = currentRole?.roleTitle ?? '';
  }

  const getCompanyProfilesPageUrl = useCallback(
    (p: CompanyProfilesPage) => {
      const searchParams = isDraft ? '?draft=true' : '';
      const getPath = () => {
        switch (p) {
          case CompanyProfilesPage.Culture:
          case CompanyProfilesPage.Jobs:
          case CompanyProfilesPage.Salaries:
          case CompanyProfilesPage.Reviews:
            return `/${ROOT_PATH}/${companySlug}/${p}${searchParams}`;
          default:
            return `/${ROOT_PATH}/${companySlug}${searchParams}`;
        }
      };

      return localize({
        path: getPath(),
        language,
      });
    },
    [companySlug, isDraft, language, localize],
  );

  const getOrigin = ():
    | 'home'
    | 'about'
    | 'culture'
    | 'jobs'
    | 'salaries'
    | 'salaries role'
    | 'reviews' => {
    switch (page) {
      case CompanyProfilesPage.About:
        return 'about';
      case CompanyProfilesPage.Culture:
        return 'culture';
      case CompanyProfilesPage.Jobs:
        return 'jobs';
      case CompanyProfilesPage.Salaries:
        return isSalariesRoleRoute ? 'salaries role' : 'salaries';
      case CompanyProfilesPage.Reviews:
        return 'reviews';
      case CompanyProfilesPage.Home:
      default:
        return 'home';
    }
  };

  const trackWriteAReviewClicked = () => {
    trackLinkEvents.trackWriteAReviewButtonClicked({
      config,
      context,
      actionOrigin: getOrigin(),
    });
  };

  const trackReviewsClicked = () => {
    trackLinkEvents.trackReviewsClicked({
      config,
      context,
      actionOrigin: getOrigin(),
    });
  };

  const trackJobsPaginationClicked = trackLinkEvents.trackPaginationClicked({
    config,
    context,
    currentPageSection: 'jobs',
  });

  const trackReviewsPaginationClicked = trackLinkEvents.trackPaginationClicked({
    config,
    context,
    currentPageSection: 'reviews',
  });

  const trackPhotosPaginationClicked = trackLinkEvents.trackPaginationClicked({
    config,
    context,
    currentPageSection: 'culture',
  });

  const trackSortOrderChanged = trackLinkEvents.trackSortOrderChanged({
    config,
    context,
  });

  const trackSeeAllPhotosButtonClicked = () =>
    trackLinkEvents.trackSeeAllPhotosButtonClicked({
      config,
      context,
    });

  const trackWebsiteClicked = () =>
    trackLinkEvents.trackWebsiteClicked({
      config,
      context,
    });

  const trackSignInClicked = () => {
    trackLinkEvents.trackSignInClicked({
      config,
      context,
    });
  };

  const trackRegisterClicked = () => {
    trackLinkEvents.trackRegisterClicked({
      config,
      context,
    });
  };

  const trackRandomCompanyProfileLinkClicked = ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => {
    trackLinkEvents.trackRandomCompanyProfileLinkClicked({
      config,
      context,
      linkedCompanyId,
    });
  };

  const tabsRef = useRef<HTMLDivElement | null>(null);
  const onActiveTabChanged = useCallback(
    (item: string | undefined) => {
      const newActiveTab = item ? (item as CompanyProfilesPage) : DEFAULT_TAB;
      void navigate(getCompanyProfilesPageUrl(newActiveTab), {
        replace: true,
      });
    },
    [DEFAULT_TAB, getCompanyProfilesPageUrl, navigate],
  );

  useEffect(() => {
    if (companyProfileModel && setCompanyId && setCompanyName) {
      setCompanyId(companyProfileModel.id);
      setCompanyName(companyProfileModel.name);
      setIsDraft(params.get('draft') === 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfileModel, params]);

  useEffect(() => {
    if (params.get('ac')) {
      onActiveTabChanged(CompanyProfilesPage.Reviews);
      setShowReviewReplyNotice(true);
    }
  }, [params, onActiveTabChanged]);

  const brandUrl = getHostName({
    site,
    staging: environment === 'staging' || environment === 'development',
    ...(consolidated ? { alternative: 'subDomainRegion' } : {}),
  });

  useEffect(() => {
    const splitedUrls = document.referrer.split(brandUrl);
    setShouldShowBackToJobSearchLink(
      splitedUrls.length === 2 && splitedUrls[1].substring(0, 4) === '/job',
    );
  }, [brandUrl]);

  return (
    <MainLayout pageWidth="medium" showHeaderDivider>
      <CompanyProfileLayoutStickyHeader
        companyName={companyProfileModel.name}
        branding={companyProfileModel.branding}
        activeTab={activeTab}
        onActiveTabChanged={onActiveTabChanged}
        shouldShowSalariesPage={shouldShowSalariesPage}
        origin={getOrigin()}
        companyProfileModel={companyProfileModel}
        aboutPageLink={getCompanyProfilesPageUrl(CompanyProfilesPage.About)}
        tabRef={tabsRef}
      />
      <Box>
        <Columns space="small" alignY="center" collapseBelow="tablet">
          <Column width="2/3">
            {shouldShowBackToJobSearchLink && (
              <Text tone="secondary">
                <TextLinkButton
                  weight="weak"
                  onClick={() => {
                    window.history.back();
                  }}
                >
                  &lt; {t('Back to job search')}
                </TextLinkButton>
              </Text>
            )}
          </Column>
          <Column width="1/3">
            <CompanySearchBar setActiveTab={setActiveTab} />
          </Column>
        </Columns>

        <CompanyBrandingSection
          model={{
            name: companyProfileModel.name,
            branding: companyProfileModel.branding,
            rating: companyProfileModel.rating,
          }}
        />

        <Box background="body" paddingY="xxsmall">
          <CompanyProfileSummary
            model={{
              name: companyProfileModel.name,
              branding: companyProfileModel.branding,
              rating: companyProfileModel.rating,
              isClaimed: companyProfileModel.isClaimed,
            }}
            trackWriteAReviewClicked={trackWriteAReviewClicked}
            navigateToReviewsTab={() => {
              trackLinkEvents.trackReviewsClicked({
                config,
                context,
                actionOrigin: getOrigin(),
              });
              onActiveTabChanged(CompanyProfilesPage.Reviews);
            }}
            navigateToAboutTab={() => {
              onActiveTabChanged(CompanyProfilesPage.About);
            }}
            aboutPageLink={getCompanyProfilesPageUrl(CompanyProfilesPage.About)}
          />
        </Box>

        <Box
          display="flex"
          justifyContent="center"
          background="body"
          padding="small"
          className={styles.mobileWriteAReviewButton}
        >
          <WriteReviewButton
            companyProfileName={companyProfileModel.name}
            origin={getOrigin()}
          />
        </Box>

        <TabsProvider
          id="company-profile"
          selectedItem={activeTab}
          onChange={(_index, item) => onActiveTabChanged(item)}
        >
          <MetaTag
            {...getMetaData(config, {
              tab: activeTab as CompanyProfilesPage,
              companyTitle: companyProfileModel.name,
              reviewCount: companyProfileModel?.rating?.count,
              companyProfileImage: companyProfileModel.branding?.logoImageUrl,
              companyProfileZone: companyProfileModel.zone,
              brandUrl,
              absoluteUrl,
              locationName: locationContextualName,
              isSalariesRoleRoute,
              roleName,
            })}
          />
          <Box
            ref={tabsRef}
            background="body"
            paddingBottom="none"
            className={styles.stickyTabs}
          >
            <Box position="relative" width="full">
              <Box className={styles.stickyWriteAReviewButton}>
                <Box
                  display="flex"
                  justifyContent="center"
                  className={styles.WriteAReviewButtonDesktop}
                >
                  <WriteReviewButton
                    companyProfileName={companyProfileModel.name}
                    origin={getOrigin()}
                  />
                </Box>
              </Box>
            </Box>
            <Tabs label="" divider="full" reserveHitArea={true}>
              <Tab item={CompanyProfilesPage.About}>{t('About')}</Tab>
              <Tab item={CompanyProfilesPage.Culture}>
                {t('Life and Culture')}
              </Tab>
              <Tab item={CompanyProfilesPage.Jobs}>{t('Jobs')}</Tab>
              {shouldShowSalariesPage && (
                <Tab item={CompanyProfilesPage.Salaries}>
                  {t('Salaries')} <Badge tone="positive">{t('New')}</Badge>
                </Tab>
              )}
              {!shouldHideReview && (
                <Tab item={CompanyProfilesPage.Reviews}>{t('Reviews')}</Tab>
              )}
            </Tabs>
          </Box>
          <Box ref={scrollToTopRef}>
            <TabPanels renderInactivePanels={false}>
              <TabPanel item={CompanyProfilesPage.About}>
                <AboutPageContainer
                  onActiveTabChanged={onActiveTabChanged}
                  getCompanyProfilesPageUrl={getCompanyProfilesPageUrl}
                  trackWriteAReviewClicked={trackWriteAReviewClicked}
                  trackReviewsClicked={trackReviewsClicked}
                  trackSeeAllPhotosButtonClicked={
                    trackSeeAllPhotosButtonClicked
                  }
                  trackWebsiteClicked={trackWebsiteClicked}
                  trackRandomCompanyProfileLinkClicked={
                    trackRandomCompanyProfileLinkClicked
                  }
                />
              </TabPanel>
              <TabPanel item={CompanyProfilesPage.Culture}>
                <CulturePageContainer
                  trackPhotosPaginationClicked={trackPhotosPaginationClicked}
                />
              </TabPanel>
              <TabPanel item={CompanyProfilesPage.Jobs}>
                <JobsPageContainer
                  trackJobsPaginationClicked={trackJobsPaginationClicked}
                />
              </TabPanel>
              {shouldShowSalariesPage && (
                <TabPanel item={CompanyProfilesPage.Salaries}>
                  <SalariesPageContainer
                    trackJobsPaginationClicked={trackJobsPaginationClicked}
                    trackRandomCompanyProfileLinkClicked={
                      trackRandomCompanyProfileLinkClicked
                    }
                    onActiveTabChanged={onActiveTabChanged}
                    trackReviewsClicked={trackReviewsClicked}
                  />
                </TabPanel>
              )}
              {!shouldHideReview && (
                <TabPanel item={CompanyProfilesPage.Reviews}>
                  <ReviewsPageContainer
                    showReviewReplyNotice={showReviewReplyNotice}
                    trackWriteAReviewClicked={trackWriteAReviewClicked}
                    trackReviewsPaginationClicked={
                      trackReviewsPaginationClicked
                    }
                    trackSortOrderChanged={trackSortOrderChanged}
                    trackSignInClicked={trackSignInClicked}
                    trackRegisterClicked={trackRegisterClicked}
                  />
                </TabPanel>
              )}
            </TabPanels>
          </Box>
        </TabsProvider>
      </Box>
    </MainLayout>
  );
}
